import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useParams } from "react-router-dom";
import { TbRefresh } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import {
  GRID_HISTORY_COLS_4,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
  GridHistoryStatusActionsCell,
  GridHistoryStatusActionsHeader,
  GridPagination,
} from "../../../../Components/Molecules/GridHistory";
import { fetchBaasTransactions } from "../../../../Services/baasTransactionsApi";
import { formatCurrency } from "../../../../Utils/formatters";
import { paginateList } from "../../Extract/extractFilters";
import { BAAS_ACCOUNTS_PATH } from "../constants";
import { BaasTransactionDetailDrawer } from "../Transactions/components/BaasTransactionDetailDrawer";
import { BaasTransactionRefundModal } from "../Transactions/components/BaasTransactionRefundModal";
import { BaasTransactionIcon } from "../Transactions/components/BaasTransactionIcon";
import { BaasTransactionStatusBadge } from "../Transactions/components/BaasTransactionStatusBadge";
import { BaasTransactionsTotalsCards } from "../Transactions/components/BaasTransactionsTotalsCards";
import { BaasExtratoFilterBar } from "./BaasExtratoFilterBar";
import {
  createDefaultBaasExtratoFilters,
  filterBaasExtratoItems,
} from "./baasExtratoFilters";

/** Same GridHistory columns as `/baas/transacoes` — Transação | Data | Valor | Status. */
const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

const ExtratoHeaderRow = () => (
  <GridHistoryHeader colSpan={4}>
    <span>{intl.get("baas.transactions.columns.transaction")}</span>
    <span>{intl.get("baas.transactions.columns.date")}</span>
    <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>
      {intl.get("baas.transactions.columns.amount")}
    </span>
    <GridHistoryStatusActionsHeader statusLabel={intl.get("baas.transactions.columns.status")} />
  </GridHistoryHeader>
);

const ExtratoRow = ({ transaction, onOpen }) => {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(transaction);
    }
  };

  const amountCents = Math.abs(Number(transaction.amount) || 0);
  const signedAmount =
    transaction.direction === "outbound" ? -amountCents : amountCents;
  const formatted = formatCurrency(amountCents, transaction.currency);
  const amountLabel =
    signedAmount > 0 ? `+${formatted}` : signedAmount < 0 ? `-${formatted}` : formatted;

  const title =
    transaction.description ||
    transaction.counterpartyName ||
    transaction.referenceId ||
    "—";
  const subtitle =
    transaction.name ||
    (transaction.currency ? String(transaction.currency).toUpperCase() : "");

  return (
    <GridHistoryRow
      colSpan={4}
      className="cursor-pointer hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(transaction)}
      onKeyDown={handleKeyDown}
    >
      <GridHistoryCell label={intl.get("baas.transactions.columns.transaction")}>
        <div className="flex min-w-0 items-center gap-3">
          <BaasTransactionIcon transaction={transaction} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
              {title}
            </p>
            {subtitle ? (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
            ) : null}
          </div>
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.transactions.columns.date")}>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {formatDate(transaction.createdAt)}
        </p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.transactions.columns.amount")} align="right">
        <p
          className={`text-sm font-semibold tabular-nums ${
            signedAmount > 0
              ? "text-emerald-600 dark:text-emerald-400"
              : signedAmount < 0
                ? "text-red-600 dark:text-red-400"
                : "text-brand-dark dark:text-gray-100"
          }`}
        >
          {amountLabel}
        </p>
      </GridHistoryCell>
      <GridHistoryStatusActionsCell
        statusLabel={intl.get("baas.transactions.columns.status")}
        statusBadge={
          <BaasTransactionStatusBadge
            status={transaction.status}
            refunded={transaction.refunded}
            returned={transaction.returned}
          />
        }
        onShowDetail={(item) => onOpen(item)}
        item={transaction}
      />
    </GridHistoryRow>
  );
};

/**
 * Account-scoped extrato panel under `/baas/contas/:accountId`.
 * Same GridHistory table + chip filters pattern as `/baas/transacoes`
 * (plus Moeda), fed by BaaS `GET …/accounts/{id}/statements`.
 */
export const BaasExtratoPage = ({
  embedded = false,
  accountId: accountIdProp,
  accountCurrencies = [],
} = {}) => {
  const { accountId: accountIdParam } = useParams();
  const accountId = accountIdProp || accountIdParam;
  const { selectedInstance } = useBaasInstance();
  const { can } = useBaasCapabilities();
  const pageLoading = useBaasPageLoading(selectedInstance?.id || accountId);
  const allowed = can("accounts.view");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filters, setFilters] = useState(createDefaultBaasExtratoFilters);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundTransaction, setRefundTransaction] = useState(null);

  const dateFromKey = filters.dateFrom
    ? filters.dateFrom.getTime?.() ?? String(filters.dateFrom)
    : "";
  const dateToKey = filters.dateTo
    ? filters.dateTo.getTime?.() ?? String(filters.dateTo)
    : "";

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance || !allowed || !accountId) {
      setItems([]);
      return undefined;
    }

    setLoading(true);
    (async () => {
      try {
        const mapped = await fetchBaasTransactions(selectedInstance, {
          accountIds: [accountId],
          startDate: filters.dateFrom || undefined,
          endDate: filters.dateTo || undefined,
          fetchAll: true,
          includePix: false,
        });
        if (!cancelled) setItems(mapped);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dateFromKey/dateToKey track filter dates
  }, [selectedInstance, allowed, refreshKey, accountId, dateFromKey, dateToKey]);

  const filteredItems = useMemo(
    () => filterBaasExtratoItems(items, filters),
    [items, filters],
  );

  const paginatedItems = useMemo(
    () => paginateList(filteredItems, page, rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const selectedDetail = useMemo(() => {
    if (!selectedTransaction) return null;
    return items.find((item) => item.id === selectedTransaction.id) ?? selectedTransaction;
  }, [selectedTransaction, items]);

  if (!accountId) {
    return <Redirect to={BAAS_ACCOUNTS_PATH} />;
  }

  if (pageLoading || loading) {
    return <BaasTablePageSkeleton columns={4} />;
  }

  if (!allowed) {
    return (
      <div className="baas-extrato min-w-0">
        <p className="text-sm text-gray-500">{intl.get("baas.extrato.permissionDenied")}</p>
      </div>
    );
  }

  return (
    <div className="baas-extrato min-w-0 space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {!embedded ? (
            <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
              {intl.get("baas.extrato.title")}
            </h1>
          ) : (
            <p className="dashboard-section-label mb-0">
              {intl.get("baas.accounts.detail.statementTitle")}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.extrato.subtitle")}
          </p>
        </div>
        <button
          type="button"
          className="btn-outline w-auto !text-xs"
          onClick={() => {
            setPage(1);
            setRefreshKey((value) => value + 1);
          }}
        >
          <TbRefresh className="h-4 w-4" />
          {intl.get("baas.extrato.refresh")}
        </button>
      </header>

      <BaasTransactionsTotalsCards items={filteredItems} />

      <BaasExtratoFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filteredItems={filteredItems}
        items={items}
        accountCurrencies={accountCurrencies}
      />

      <div className="app-card overflow-hidden">
        {paginatedItems.length === 0 ? (
          <div className="app-empty-state m-4">{intl.get("baas.extrato.empty")}</div>
        ) : (
          <GridHistoryContainer gridCols={GRID_HISTORY_COLS_4}>
            <ExtratoHeaderRow />
            {paginatedItems.map((transaction) => (
              <ExtratoRow
                key={transaction.id}
                transaction={transaction}
                onOpen={setSelectedTransaction}
              />
            ))}
          </GridHistoryContainer>
        )}

        <GridPagination
          total={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(1);
          }}
          currentPageCount={paginatedItems.length}
          itemLabel={intl.get("baas.transactions.paginationLabel")}
        />
      </div>

      <BaasTransactionDetailDrawer
        transaction={selectedDetail}
        onClose={() => setSelectedTransaction(null)}
        onRefundRequest={(txn) => {
          setSelectedTransaction(null);
          setRefundTransaction(txn);
        }}
      />
      <BaasTransactionRefundModal
        open={Boolean(refundTransaction)}
        transaction={refundTransaction}
        onClose={() => setRefundTransaction(null)}
        onSuccess={() => {
          setRefundTransaction(null);
          setRefreshKey((value) => value + 1);
        }}
      />
    </div>
  );
};

export default BaasExtratoPage;
